import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import { siteData } from '../../data/mock';

const NavLink = ({ href, className, style, onClick, children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleClick = (e) => {
    // Handle /#section links (hash on homepage)
    if (href.startsWith('/#')) {
      e.preventDefault();
      const sectionId = href.substring(2);
      if (location.pathname === '/') {
        // Already on homepage — scroll to section
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else {
        // Navigate to homepage, then scroll
        navigate('/', { state: { scrollTo: sectionId } });
      }
      if (onClick) onClick(e);
      return;
    }
    if (onClick) onClick(e);
  };

  if (href.startsWith('/') && !href.startsWith('/#')) {
    return (
      <Link to={href} className={className} style={style} onClick={onClick}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} className={className} style={style} onClick={handleClick}>
      {children}
    </a>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { nav } = siteData;
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle scroll-to after navigation from another page
  useEffect(() => {
    if (location.state?.scrollTo) {
      const timeout = setTimeout(() => {
        const el = document.getElementById(location.state.scrollTo);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 ${
        scrolled
          ? 'bg-[#faf9f6]/80 backdrop-blur-[16px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] border-b border-stone-200/40'
          : 'bg-transparent'
      }`}
      style={{ transition: 'background-color 0.3s, box-shadow 0.3s, border-color 0.3s' }}
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <Link
            to="/"
            className="font-heading text-[1.15rem] font-bold text-stone-900 tracking-tight select-none"
          >
            {nav.logo}
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-9">
            <nav className="flex items-center gap-9">
              {nav.links.map((link) => (
                <NavLink
                  key={link.label}
                  href={link.href}
                  className="text-[0.84rem] text-stone-500 hover:text-stone-800 font-medium"
                  style={{ transition: 'color 0.2s' }}
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>

            {/* CTA block */}
            <div className="flex flex-col items-center ml-3">
              <span className="text-[0.68rem] text-stone-400 font-medium leading-none mb-1.5">
                {nav.cta.sublabel}
              </span>
              <Button
                variant="outline"
                asChild
                className="border-[#2d6a5a]/30 text-[#2d6a5a] hover:bg-[#2d6a5a] hover:text-white hover:border-[#2d6a5a] rounded-full px-5 h-8 text-[0.8rem] font-medium"
                style={{ transition: 'background-color 0.2s, color 0.2s, border-color 0.2s' }}
              >
                <Link to={nav.cta.href}>{nav.cta.label}</Link>
              </Button>
            </div>
          </div>

          <button
            className="md:hidden p-2 text-stone-500 hover:text-stone-800"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-[#faf9f6]/95 backdrop-blur-[20px] border-t border-stone-200/40 px-6 py-5">
          <nav className="flex flex-col gap-1">
            {nav.links.map((link) => (
              <NavLink
                key={link.label}
                href={link.href}
                className="text-[0.9rem] text-stone-600 hover:text-stone-900 py-2.5 font-medium"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </NavLink>
            ))}
            <Button
              asChild
              className="bg-[#2d6a5a] hover:bg-[#245549] text-white mt-3 rounded-full text-[0.84rem] font-medium h-10"
              style={{ transition: 'background-color 0.2s' }}
            >
              <Link to={nav.cta.href} onClick={() => setMobileOpen(false)}>
                {nav.cta.label}
              </Link>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};
